import { createZITADELAuth } from "@zitadel/vue";
import { User } from "oidc-client";
import store from "@/store";

const getLoginHint = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const loginHint =
    urlParams.get("login_hint") || urlParams.get("user_email") || null;

  if (loginHint) {
    urlParams.delete("login_hint");
    urlParams.delete("user_email");

    const newUrl = urlParams.toString()
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;

    window.history.replaceState({}, "", newUrl);
  }

  return loginHint;
};

export const zitadelAuth = createZITADELAuth(
  {
    issuer: window.runtimeConfig.ZITADEL_ISSUER,
    client_id: window.runtimeConfig.ZITADEL_CLIENT_ID,
  },
  "zitadel",
  0,
  `${window.location.origin}/`,
  {
    scope: "openid profile email offline_access",
    revokeAccessTokenOnSignout: true,
    post_logout_redirect_uri: `${window.runtimeConfig?.ZITADEL_ISSUER}/ui/login/logout/done`,
    monitorSession: true,
    checkSessionInterval: 2000,
    extraQueryParams: {
      login_hint: getLoginHint(),
    },
  }
);

class AuthService {
  constructor() {
    this.setupZitadelListeners();
    this.manualTokenName = "zitadelmanualtokens";
  }

  setupZitadelListeners() {
    if (zitadelAuth.oidcAuth.events) {
      zitadelAuth.oidcAuth.events.addAccessTokenExpired(
        this.onTokenExpired.bind(this)
      );

      // Also listen for silent renew errors
      zitadelAuth.oidcAuth.events.addSilentRenewError((error) => {
        console.log("Silent renew error caught:", error);
        this.onTokenExpired();
      });
    }
  }
  async onTokenExpired() {
    try {
      await zitadelAuth.oidcAuth.startSilentRenew()
    } catch (refreshError) {
      // Check if we're in a Bobbie iframe
      const isInIframe = window.self !== window.top;
      const isBobbieContext = store.state.auth?.isBobbieContext || false;
      const isInIFrameContext = store.state.auth?.isInIFrameContext || false;
      if (isInIframe && (isBobbieContext || isInIFrameContext)) {
        // Don't logout/redirect, show the auth guard
        this.showIframeAuthGuard();
        return;
      }

      await this.logout();
    }
  }

  showIframeAuthGuard() {
    // Redirect to the auth required route
    import("@/router").then((module) => {
      const router = module.default;
      router.push("/bobbie-auth-required");
    });
  }

  async logout() {
    await zitadelAuth.oidcAuth.signOut();
  }

  async manualUserLogIn(userData) {
    const expiresAt = Math.floor(Date.now() / 1000) + userData.expiresIn;
    const profile = this._decodeJWT(userData.idToken);
    const user = new User({
      access_token: userData.accessToken,
      id_token: userData.idToken,
      refresh_token: userData.refreshToken,
      scope: userData.scope,
      token_type: userData.tokenType,
      expires_at: expiresAt,
      profile: {
        ...profile,
        azp: window.runtimeConfig?.ZITADEL_CLIENT_ID,
      },
    });
    await zitadelAuth.oidcAuth.mgr.storeUser(user);
    await zitadelAuth.oidcAuth.mgr.events.load(user);
  }

  get oidcName() {
    return zitadelAuth.oidcAuth.authName;
  }

  get oidcAuth() {
    return zitadelAuth.oidcAuth;
  }

  get userEmail() {
    return zitadelAuth.oidcAuth.user.profile.email;
  }

  get accessToken() {
    try {
      return zitadelAuth.oidcAuth.accessToken;
    } catch (error) {
      console.error("Failed to get access token:", error);
      return null;
    }
  }
  _decodeJWT(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  }
}

export const authService = new AuthService();

import { ElMessage } from "element-plus";

export const noCurrentProject = function () {
  ElMessage.error({
    message: "No current project found.",
    showClose: true,
  });
  setTimeout(() => {
    window.location.href = `${window.runtimeConfig?.AFP_URL}?login_hint=${authService.userEmail}`;
  }, 1000);
};
